import type { CSSProperties, ReactNode } from 'react';
import { PILLS, theme } from '../theme.js';

export function pillStyle(status: string): CSSProperties {
  const [background, border, color] = PILLS[status] ?? PILLS.DRAFT!;
  return {
    background,
    border: `1px solid ${border}`,
    color,
    borderRadius: theme.radiusPill,
    padding: '3px 9px',
    fontSize: '10.5px',
    fontWeight: 700,
    letterSpacing: '.06em',
    whiteSpace: 'nowrap',
    display: 'inline-block',
  };
}

export function Pill({ status, label }: { status: string; label?: string }) {
  return <span style={pillStyle(status)}>{label ?? status}</span>;
}

export function Card({ children, style }: { children: ReactNode; style?: CSSProperties }) {
  return (
    <div
      style={{
        background: theme.surface,
        border: 0,
        borderRadius: theme.radiusCard,
        boxShadow: theme.shadowCard,
        overflow: 'hidden',
        ...style,
      }}
    >
      {children}
    </div>
  );
}

export function CardHeader({ children, style }: { children: ReactNode; style?: CSSProperties }) {
  return (
    <div
      style={{
        padding: '12px 16px',
        borderBottom: `1px solid ${theme.dividerSoft}`,
        fontWeight: 600,
        fontSize: '13px',
        background: theme.surfaceHeader,
        ...style,
      }}
    >
      {children}
    </div>
  );
}

export function PageTitle({ title, sub }: { title: string; sub?: string }) {
  return (
    <div>
      <h1 style={{ margin: 0, fontSize: '22px', fontWeight: 600, letterSpacing: '-.01em' }}>
        {title}
      </h1>
      {sub && (
        <div style={{ color: theme.textMuted, marginTop: '5px', fontSize: '13px' }}>{sub}</div>
      )}
    </div>
  );
}

export const buttonPrimary: CSSProperties = {
  background: theme.nav,
  color: '#fff',
  border: 0,
  borderRadius: theme.radiusControl,
  padding: '10px 16px',
  fontWeight: 600,
  fontSize: '13px',
  whiteSpace: 'nowrap',
  cursor: 'pointer',
};

export const buttonQuiet: CSSProperties = {
  background: theme.surfaceSunken,
  border: `1px solid ${theme.border}`,
  borderRadius: theme.radiusControl,
  padding: '8px 12px',
  fontSize: '12.5px',
  fontWeight: 600,
  cursor: 'pointer',
  color: theme.text,
};

export const buttonCard: CSSProperties = {
  background: theme.surface,
  border: 0,
  borderRadius: theme.radiusCard,
  boxShadow: theme.shadowCard,
  padding: '9px 13px',
  fontSize: '12.5px',
  fontWeight: 600,
  cursor: 'pointer',
  color: theme.text,
};

export function Banner({ children, tone = 'good' }: { children: ReactNode; tone?: 'good' | 'warn' }) {
  const good = tone === 'good';
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        background: good ? theme.goodBg : '#F8EDEC',
        border: `1px solid ${good ? theme.goodBorder : '#EBD2D1'}`,
        borderRadius: theme.radiusControl,
        padding: '11px 14px',
        marginBottom: '14px',
        fontSize: '12.5px',
        color: good ? theme.goodDeep : '#8A3230',
      }}
    >
      <span
        style={{
          width: '7px',
          height: '7px',
          borderRadius: '50%',
          background: good ? theme.good : theme.bad,
          flex: '0 0 auto',
        }}
      />
      {children}
    </div>
  );
}
